// Aritmética de semanas de la liquidación a repartidores.
//
// La semana es DOMINGO–SÁBADO, no lunes–domingo. No es una preferencia estética: es la
// semana con la que el backend agrupa el documento tributario del repartidor
// (`sundaySaturdayWeek` en microservice-billing/driver-billing-document.service.ts), así
// que navegar por semanas dom–sáb hace que el rango de la vista coincida exacto con la
// unidad en que existe la factura/boleta. Ojo: `cobroHelpers.DATE_PRESETS` usa semanas
// lun–dom para las NOTAS DE COBRO — son dos calendarios distintos a propósito.
//
// Todas las fechas se manejan como 'YYYY-MM-DD' en hora LOCAL. `new Date('2026-07-20')`
// se parsea como UTC y en tz negativas devuelve el día anterior; por eso se construye
// siempre con `new Date(y, m - 1, d)`.

const pad = (n) => String(n).padStart(2, '0');

/** Date local → 'YYYY-MM-DD'. */
export const toYMD = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** 'YYYY-MM-DD' → Date local a medianoche. */
export const fromYMD = (s) => {
  const [y, m, d] = String(s).split('-').map(Number);
  return new Date(y, m - 1, d);
};

/** Hoy en 'YYYY-MM-DD' (tz del navegador del operador). */
export const todayYMD = () => toYMD(new Date());

/** "lun 27 jul" — más escaneable que dd-mm-yyyy en listas de días. */
export const dayLabel = (ymd) => {
  try {
    return new Intl.DateTimeFormat('es-CL', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    }).format(fromYMD(ymd));
  } catch {
    return ymd;
  }
};

/** Semana dom–sáb que CONTIENE `dateStr`. Devuelve { from, to }. */
export const weekOf = (dateStr) => {
  const start = fromYMD(dateStr);
  start.setDate(start.getDate() - start.getDay()); // getDay(): 0 = domingo
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { from: toYMD(start), to: toYMD(end) };
};

/** Semana en curso. */
export const currentWeek = (today = todayYMD()) => weekOf(today);

/** Desplaza una semana `delta` semanas (negativo = hacia atrás). */
export const shiftWeeks = (range, delta) => {
  const d = fromYMD(range.from);
  d.setDate(d.getDate() + delta * 7);
  return weekOf(toYMD(d));
};

/** Semana anterior completa. Equivale al preset por defecto histórico de la vista. */
export const previousWeek = (today = todayYMD()) => shiftWeeks(currentWeek(today), -1);

/** ¿El rango [from, to] es exactamente una semana dom–sáb? */
export const isFullWeek = (from, to) => {
  if (!from || !to) return false;
  const w = weekOf(from);
  return w.from === from && w.to === to;
};

/**
 * ¿La semana que empieza en `from` todavía no cierra?
 *
 * Importa para la vista: el backend RECHAZA la subida del documento tributario de una
 * semana en curso (el monto aún cambia con cada entrega, así que el documento saldría
 * por una cifra equivocada). Marcar "falta su factura/boleta" en la semana actual sería
 * una falsa alarma: el repartidor no puede subirla aunque quiera.
 */
export const isWeekStillOpen = (from, today = todayYMD()) =>
  from >= currentWeek(today).from;

/**
 * Etiqueta compacta de la semana: "20 – 26 jul 2026", "28 sep – 4 oct 2026",
 * "28 dic 2025 – 3 ene 2026". Solo repite mes/año cuando la semana los cruza.
 */
export const weekLabel = (from, to, locale = 'es-CL') => {
  const a = fromYMD(from);
  const b = fromYMD(to);
  const day = (d) => String(d.getDate());
  // Intl agrega punto en algunos locales ("jul."); molesta dentro de un rango corto.
  const mon = (d) => d.toLocaleDateString(locale, { month: 'short' }).replace('.', '');

  if (a.getFullYear() !== b.getFullYear()) {
    return `${day(a)} ${mon(a)} ${a.getFullYear()} – ${day(b)} ${mon(b)} ${b.getFullYear()}`;
  }
  const head =
    a.getMonth() === b.getMonth()
      ? `${day(a)} – ${day(b)} ${mon(b)}`
      : `${day(a)} ${mon(a)} – ${day(b)} ${mon(b)}`;
  return `${head} ${a.getFullYear()}`;
};

/**
 * ISO datetime → "26 jul, 14:35" (fecha corta local + hora). A diferencia de
 * dayLabel, recibe un instante completo (uploadedAt), no un 'YYYY-MM-DD'.
 * Devuelve null si el valor falta o no parsea (Intl tira RangeError con
 * fechas inválidas; acá nunca debe propagarse a la vista).
 */
export const dateTimeLabel = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return new Intl.DateTimeFormat('es-CL', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(d);
  } catch {
    return null;
  }
};

/**
 * Días entre el día LOCAL de la subida y el cierre de la semana (`weekEnd`,
 * sábado). Se compara por día calendario del operador — `toYMD(new Date(iso))`,
 * no `iso.slice(0, 10)`: el corte UTC inflaría la demora para subidas de la
 * tarde/noche en Chile. El mínimo real es 1 (el backend rechaza subir con la
 * semana abierta). Devuelve null si algún dato falta o no parsea.
 */
export const daysAfterClose = (iso, weekEnd) => {
  if (!iso || !weekEnd) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const close = fromYMD(weekEnd);
  if (Number.isNaN(close.getTime())) return null;
  return Math.round((fromYMD(toYMD(d)) - close) / 86400000);
};
